import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Bell, CheckCircle, XCircle, Clock,
  Filter, Search, RefreshCw
} from 'lucide-react';
import { getAlerts, acknowledgeAlert, resolveAlert, dismissAlert } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Demo alerts for visualization
  const demoAlerts = [
    {
      _id: '1',
      alertType: 'anomaly',
      severity: 'critical',
      title: 'Revenue Anomaly Detected',
      message: 'Unexpected 35% drop in revenue for Northeast region compared to forecast',
      details: { metric: 'revenue', deviationPercentage: -35 },
      status: 'active',
      createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString()
    },
    {
      _id: '2',
      alertType: 'threshold',
      severity: 'critical',
      title: 'Churn Risk Threshold Exceeded',
      message: '47 customers have moved into critical risk category this week',
      details: { metric: 'churn_score', actualValue: 47 },
      status: 'active',
      createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString()
    },
    {
      _id: '3',
      alertType: 'trend_change',
      severity: 'warning',
      title: 'Demand Pattern Shift',
      message: 'Product category "Electronics" showing unusual demand pattern',
      details: { metric: 'demand', deviationPercentage: 22 },
      status: 'active',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
    },
    {
      _id: '4',
      alertType: 'forecast_deviation',
      severity: 'warning',
      title: 'Forecast Accuracy Alert',
      message: 'Q3 actual results deviating from predictions by more than 15%',
      details: { metric: 'forecast_accuracy', deviationPercentage: 18 },
      status: 'acknowledged',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString()
    },
    {
      _id: '5',
      alertType: 'data_quality',
      severity: 'info',
      title: 'Data Quality Check Passed',
      message: 'Monthly data validation completed with 99.2% completeness score',
      details: { metric: 'data_quality', actualValue: 99.2 },
      status: 'resolved',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString()
    },
    {
      _id: '6',
      alertType: 'anomaly',
      severity: 'info',
      title: 'Positive Revenue Spike',
      message: 'Weekend sales 45% above expected for promotional period',
      details: { metric: 'revenue', deviationPercentage: 45 },
      status: 'resolved',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString()
    }
  ];

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const response = await getAlerts().catch(() => null);
      setAlerts(response?.alerts?.length > 0 ? response.alerts : demoAlerts);
    } catch (err) {
      setAlerts(demoAlerts);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (id) => {
    try {
      await acknowledgeAlert(id).catch(() => {});
      setAlerts(alerts.map(a => a._id === id ? { ...a, status: 'acknowledged' } : a));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  const handleResolve = async (id) => {
    try {
      await resolveAlert(id).catch(() => {});
      setAlerts(alerts.map(a => a._id === id ? { ...a, status: 'resolved' } : a));
    } catch (err) {
      console.error('Error resolving alert:', err);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await dismissAlert(id).catch(() => {});
      setAlerts(alerts.map(a => a._id === id ? { ...a, status: 'dismissed' } : a));
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter !== 'all' && alert.status !== filter) return false;
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
    if (searchTerm && !alert.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !alert.message.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const alertCounts = {
    total: alerts.length,
    active: alerts.filter(a => a.status === 'active').length,
    critical: alerts.filter(a => a.severity === 'critical' && a.status === 'active').length,
    warning: alerts.filter(a => a.severity === 'warning' && a.status === 'active').length
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle size={18} />;
      case 'warning': return <Bell size={18} />;
      default: return <CheckCircle size={18} />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-danger',
      acknowledged: 'badge-warning',
      resolved: 'badge-success',
      dismissed: 'badge-info'
    };
    return badges[status] || 'badge-info';
  };

  return (
    <div className="alerts-page">
      <div className="page-header">
        <h2>Alerts & Notifications</h2>
        <p>Monitor anomalies, thresholds, and forecast deviations</p>
      </div>

      {/* Summary Cards */}
      <div className="kpi-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="kpi-card">
          <div className="kpi-icon blue">
            <Bell size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Total Alerts</p>
            <h3 className="kpi-value">{alertCounts.total}</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon red">
            <AlertTriangle size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Critical</p>
            <h3 className="kpi-value">{alertCounts.critical}</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon yellow">
            <Clock size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Active</p>
            <h3 className="kpi-value">{alertCounts.active}</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon green">
            <CheckCircle size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Resolved Today</p>
            <h3 className="kpi-value">{alerts.filter(a => a.status === 'resolved').length}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>

          <div className="tabs" style={{ marginBottom: 0 }}>
            {['all', 'active', 'acknowledged', 'resolved'].map((status) => (
              <button
                key={status}
                className={`tab ${filter === status ? 'active' : ''}`}
                onClick={() => setFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          <select
            className="form-select"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          <button className="btn btn-outline" onClick={loadAlerts}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="empty-state">
            <Bell size={64} />
            <p>No alerts found</p>
            <p style={{ fontSize: '0.875rem' }}>Adjust your filters or check back later</p>
          </div>
        ) : (
          <div>
            {filteredAlerts.map((alert) => (
              <div
                key={alert._id}
                className="alert-item"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '1.25rem 1.5rem',
                  borderBottom: '1px solid #e5e7eb',
                  gap: '1rem'
                }}
              >
                <div className={`alert-icon ${alert.severity}`}>
                  {getSeverityIcon(alert.severity)}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontWeight: 600, color: '#111827' }}>{alert.title}</h4>
                    <span className={`badge ${getStatusBadge(alert.status)}`}>
                      {alert.status}
                    </span>
                    <span className={`badge badge-${alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}`}>
                      {alert.severity}
                    </span>
                  </div>
                  <p style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    {alert.message}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                    <span>Type: {alert.alertType?.replace('_', ' ')}</span>
                    {alert.details?.deviationPercentage && (
                      <span>Deviation: {alert.details.deviationPercentage > 0 ? '+' : ''}{alert.details.deviationPercentage}%</span>
                    )}
                    <span>{formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}</span>
                  </div>
                </div>

                {alert.status === 'active' && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleAcknowledge(alert._id)}
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      Acknowledge
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleResolve(alert._id)}
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      Resolve
                    </button>
                  </div>
                )}

                {alert.status === 'acknowledged' && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-success"
                      onClick={() => handleResolve(alert._id)}
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      Resolve
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleDismiss(alert._id)}
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Alerts;
